<template>
  <Component
    :is="maxSmall ? Drawer : Dialog"
    :open="open"
    @update:open="handleUpdateOpen"
    showFooter
    size="full"
    v-bind="maxSmall ? {} : { side: 'default', teleport: false }"
  >
    <slot />
    <template #header>
      <NotificationSettingsHeader ref="dialog" />
    </template>
    <template #content>
      <div class="grow md:py-4 md:px-5 overflow-y-auto">
        <Accordion :items="types" type="multiple">
          <template #default="{ item }">
            <BuildingToggleList
              :buildings="buildings"
              :settings="options"
              v-model:values="values[item.value]"
              size="notification-settings"
              class="px-3 pb-4"
            />
          </template>
        </Accordion>
      </div>
    </template>
    <template #footer>
      <NotificationSettingsFooter
        :dirty="isDirty"
        :busy="busy"
        @save="handleSave"
      />
    </template>
  </Component>
</template>

<script setup>
import { computed, inject, ref, toRaw, watchEffect } from "vue";
import { cloneDeep, isEqual } from "lodash";
import { Dialog } from "@/SharedComponents/Dialog";
import { Drawer } from "@/SharedComponents/Drawer";
import { Accordion } from "@/SharedComponents/Accordion";
import { BuildingToggleList } from "@/SharedComponents/BuildingToggleList";
import {
  NotificationSettingsFooter,
  NotificationSettingsHeader,
} from "@/Pages/Notifications/Partials";
import { useBreakpoints } from "@/Composables/useBreakpoints";
import {
  useNotificationSettings,
  useUpdateNotificationSettings,
} from "@/Composables/useNotifications";
import { useI18n } from "vue-i18n";

const { maxSmall } = useBreakpoints();
const { t } = useI18n();

const dialog = ref(null);

const emit = defineEmits(["update:open"]);

const props = defineProps({
  open: { type: Boolean, default: undefined },
  buildings: { type: Array, default: () => [] },
});

const _open = ref(props.open);
watchEffect(() => {
  _open.value = props.open;
});

const values = ref({});

const { isLoading, data } = useNotificationSettings();
watchEffect(() => {
  if (!isLoading.value && _open.value) {
    values.value = cloneDeep(toRaw(data.value));
  }
});

const { hasPermission, permissions } = inject("permissions");
const isServiceProvider = hasPermission(permissions.limitedAccess);

const types = computed(() => [
  {
    value: "new_problem_or_upvote",
    icon: "message-plus",
    title: t(
      "components.notifications.settings.types.new_problem_or_upvote.title"
    ),
    description: t(
      "components.notifications.settings.types.new_problem_or_upvote.description"
    ),
    disabled: isServiceProvider,
  },
  {
    value: "problem_assigned",
    icon: "user-single-checked",
    title: t("components.notifications.settings.types.task_assigned.title"),
    description: t(
      "components.notifications.settings.types.task_assigned.description"
    ),
  },
  {
    value: "new_comment",
    icon: "chat",
    title: t("components.notifications.settings.types.new_task_comment.title"),
    description: t(
      "components.notifications.settings.types.new_task_comment.description"
    ),
  },
  {
    value: "tagged_in_comment",
    icon: "mention",
    title: t("components.notifications.settings.types.tagged_in_comment.title"),
    description: t(
      "components.notifications.settings.types.tagged_in_comment.description"
    ),
  },
]);

const options = computed(() => [
  {
    key: "in_app_notification_settings",
    label: t("components.notifications.settings.options.in_app.label"),
    default: true,
    icon: "bell-default",
  },
  {
    key: "push_notification_settings",
    label: t("components.notifications.settings.options.push.label"),
    default: false,
    icon: "device-phone",
  },
  {
    key: "mail_notification_settings",
    label: t("components.notifications.settings.options.email.label"),
    default: false,
    icon: "email",
  },
]);

const isDirty = computed(() => {
  return !isEqual(toRaw(data.value), values.value);
});

const { isPending: busy, mutateAsync: updateNotificationSettings } =
  useUpdateNotificationSettings();

function handleSave() {
  updateNotificationSettings({
    buildings: props.buildings,
    values: values.value,
  }).then(() => {
    dialog.value?.close();
  });
}

function handleUpdateOpen(value) {
  _open.value = value;
  emit("update:open", value);
}
</script>
